// Who is a threat, and who is verified (plan 5B, R1 and R6). Pure: the same function
// runs for a sync, a join and a lead. `assess` matches ITCBot's
// `perform_and_format_threat_assessment` on every case in `packages/rsi/fixtures/cases.json`,
// proven in tests/threats-parity.test.ts. Python's sets have no order, so reasons are sorted.

/** One organisation on a public profile. */
export interface AssessedOrg {
  symbol: string;
  activities: readonly string[];
}

export interface AssessInput {
  /** What the public profile showed, or null when there was none. */
  profile: { orgs: readonly AssessedOrg[]; redactedOrgCount: number } | null;
  /** Symbols of the orgs they've been seen in. A member's `org_history`; a lead has none. */
  history: readonly string[];
  /** Symbols that are hostile (the setting). Compared without regard to case. */
  hostileOrgs: readonly string[];
  /** Activities that are threats (the setting). Compared without regard to case. */
  threatAffiliations: readonly string[];
  /** Whether they're in the org now. */
  isMember: boolean;
  /** Their visibility on the org's roster, if they're on it. */
  visibility: string;
}

export interface Assessment {
  /** Sorted. Empty when nothing was found. */
  reasons: string[];
  /** Sorted symbols, upper-case. */
  hostileOrgs: string[];
  /** Threat activities by org symbol, each sorted and capitalised. */
  activities: Record<string, string[]>;
}

export const NO_PUBLIC_PROFILE = 'No Public Profile';

/** Python's `str.capitalize()`: the first letter up, the rest down. */
const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

/** Python's `sorted()` order. It differs from JavaScript's only for characters outside the BMP, which symbols and activities don't use. */
const byCodePoint = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/**
 * R1: assesses a public profile. A hostile org counts if they're in it now or have been;
 * a threat activity only if they're in it now; redacted orgs count, less one for a member
 * whose roster visibility is Redacted, as that one is ours.
 */
export function assess(input: AssessInput): Assessment {
  if (input.profile === null) return { reasons: [NO_PUBLIC_PROFILE], hostileOrgs: [], activities: {} };

  const hostile = new Set(input.hostileOrgs.map((symbol) => symbol.toUpperCase()));
  const threats = new Set(input.threatAffiliations.map((activity) => activity.toLowerCase()));
  const known = new Set([...input.profile.orgs.map((org) => org.symbol.toUpperCase()), ...input.history.map((symbol) => symbol.toUpperCase())]);
  const hostileFound = [...known].filter((symbol) => hostile.has(symbol)).sort(byCodePoint);

  const found = new Map<string, string[]>();
  for (const org of input.profile.orgs) {
    const symbol = org.symbol.toUpperCase();
    for (const activity of new Set(org.activities.map((name) => name.toLowerCase()))) {
      if (!threats.has(activity)) continue;
      found.set(symbol, [...(found.get(symbol) ?? []), capitalize(activity)]);
    }
  }
  const activities: Record<string, string[]> = {};
  for (const [symbol, names] of [...found].sort(([a], [b]) => byCodePoint(a, b))) activities[symbol] = names.sort(byCodePoint);

  const redacted = input.isMember && input.visibility === 'Redacted' ? Math.max(0, input.profile.redactedOrgCount - 1) : input.profile.redactedOrgCount;

  const reasons: string[] = [];
  if (redacted > 0) reasons.push(`${String(redacted)} Redacted Org${redacted > 1 ? 's' : ''}`);
  for (const symbol of hostileFound) reasons.push(`Hostile Org: ${symbol}`);
  for (const [symbol, names] of Object.entries(activities)) reasons.push(`Threat Activity (${names.join(', ')}): ${symbol}`);
  return { reasons: reasons.sort(byCodePoint), hostileOrgs: hostileFound, activities };
}

/** What R6 reads of a profile. */
export interface VerifiedInput {
  linkState: '' | 'verified' | 'pre_migration';
  orgStatus: 'none' | 'member' | 'affiliate' | 'pending';
  isThreat: boolean;
  permanentThreat: boolean;
  agreement: '' | 'agreed' | 'rejected';
}

/** In the org, as ITCBot counts it: a roster row of either type. */
export const inOrg = (orgStatus: VerifiedInput['orgStatus']): boolean => orgStatus === 'member' || orgStatus === 'affiliate';

/** R6: linked, in the org, not a permanent threat, and either no threat or an agreement to the anti-piracy rules. */
export function isVerified(profile: VerifiedInput): boolean {
  return profile.linkState !== '' && inOrg(profile.orgStatus) && !profile.permanentThreat && (!profile.isThreat || profile.agreement === 'agreed');
}

// --- Role sources (plan 5C, R5) ---------------------------------------------------------

/** The sources Members declares for role links, by name, each with the label an admin picks it by. */
export const ROLE_SOURCES = {
  linked: 'Linked to an RSI handle',
  verified: 'Verified: linked, in the org, and no threat held against them (R6)',
  in_org: 'In the org, as a member or an affiliate',
  threat: 'Flagged as a threat',
  agreed: 'Agreed to the anti-piracy rules',
  rejected: 'Refused the anti-piracy rules',
  permanent_threat: 'A permanent threat',
  hostile_org: 'In a hostile org',
} as const;
export type RoleSourceName = keyof typeof ROLE_SOURCES;
export const ROLE_SOURCE_NAMES = Object.keys(ROLE_SOURCES) as RoleSourceName[];

/** What the sources read of a profile: R6's facts, and the orgs the scan found hostile. */
export interface RoleSourceInput extends VerifiedInput {
  hostileOrgs: readonly string[];
}

/**
 * R5: the role sources a profile is in. `verified` is exactly `isVerified`, so a role
 * granted to the verified is granted by the same rule that a sync, a join and a lead use.
 */
export function roleSourcesOf(profile: RoleSourceInput): RoleSourceName[] {
  const rules: Record<RoleSourceName, boolean> = {
    linked: profile.linkState !== '',
    verified: isVerified(profile),
    in_org: inOrg(profile.orgStatus),
    threat: profile.isThreat,
    agreed: profile.agreement === 'agreed',
    rejected: profile.agreement === 'rejected',
    permanent_threat: profile.permanentThreat,
    hostile_org: profile.hostileOrgs.length > 0,
  };
  return ROLE_SOURCE_NAMES.filter((name) => rules[name]);
}
